<script lang="ts">
	/**
	 * All page metadata, in one place.
	 *
	 * Replaces Yoast: title, description, canonical, `hreflang` alternates,
	 * Open Graph, Twitter cards and JSON-LD. Every page renders this so no route
	 * can quietly ship without a canonical or description.
	 *
	 * Canonicals are built from the passed `path`, never from the current URL —
	 * the build emits `foo.html`, and using the request URL would advertise a
	 * URL the site is not served at.
	 */
	import { SITE_NAME, DEFAULT_OG_IMAGE, absolute } from './config';
	/**
	 * Generated social cards, keyed by canonical path.
	 *
	 * Written by `scripts/og-images.mjs`, which draws each page's own title on
	 * the Dutch Passion template. Only pages that have no photograph of their own
	 * appear here — a strain page unfurls with its bud shot and an article with
	 * its hero, and neither is improved by replacing it with typography.
	 *
	 * Consulted rather than imported per route so that no page has to remember to
	 * pass one: every page already gives this component its `path`, which is the
	 * only key the lookup needs.
	 */
	import OG_CARDS from './og-images.json';
	/*
	 * `HREFLANG` and `OG_LOCALE` come straight from `$lib/i18n`, not through
	 * `$lib/seo/alternates`. Both modules re-export the same tables, but
	 * `alternates` also imports the whole WooCommerce snapshot to compute the
	 * pairings — and importing it from a component pulled ~3.5 MB of catalogue
	 * JSON into the client bundle of every page. The alternates themselves now
	 * arrive as a prop, resolved on the server; only the type is imported here,
	 * and types are erased.
	 */
	import { HREFLANG, OG_LOCALE } from '$lib/i18n';
	import type { Alternate } from './alternates';
	import type { SiteLocale } from '$lib/services/products';

	let {
		title,
		description,
		path,
		locale = 'en',
		image,
		imageAlt,
		type = 'website',
		alternates = [],
		noindex = false,
		brandSuffix = true,
		publishedTime,
		modifiedTime,
		jsonLd
	}: {
		title: string;
		description?: string;
		path: string;
		locale?: SiteLocale;
		image?: string;
		/** Falls back to the page title, which beats an empty alt on a social card. */
		imageAlt?: string;
		type?: 'website' | 'article' | 'product';
		alternates?: Alternate[];
		noindex?: boolean;
		/**
		 * Append ` | Dutch Passion Thailand` to the title.
		 *
		 * Off for blog posts. Their titles already run 46–59 characters, and the
		 * 25-character suffix pushed every one of them past the ~60 Google shows —
		 * spending a third of the visible title on a brand name that is already in
		 * `og:site_name` and the URL.
		 */
		brandSuffix?: boolean;
		publishedTime?: string;
		modifiedTime?: string;
		/**
		 * One entity, or several to be emitted as a single `@graph`.
		 *
		 * A graph rather than one `<script>` per type: entities reference each
		 * other by `@id`, so a product can point at the publisher instead of
		 * restating it. See `$lib/seo/schema`.
		 */
		jsonLd?: Record<string, unknown> | Record<string, unknown>[];
	} = $props();

	const canonical = $derived(absolute(path));
	/** The generated card for this page, if it has one and the caller named no image. */
	const card = $derived(image ? undefined : (OG_CARDS as Record<string, string>)[path]);
	const ogImage = $derived(absolute(image ?? card ?? DEFAULT_OG_IMAGE));

	/**
	 * The brand stem, not the full site name.
	 *
	 * Testing `title.includes(SITE_NAME)` looked right but let a double suffix
	 * through: two post `seoTitle`s already end `| Dutch Passion`, which does not
	 * contain "Dutch Passion Thailand", so the suffix was appended anyway and
	 * produced "… | Dutch Passion | Dutch Passion Thailand" at 84 characters.
	 * Matching the stem catches both spellings, and a page that wants no suffix at
	 * all (because 60 characters will not fit) can still opt out by including the
	 * brand itself.
	 */
	const BRAND = 'Dutch Passion';
	const fullTitle = $derived(
		!brandSuffix || title.includes(BRAND) ? title : `${title} | ${SITE_NAME}`
	);

	/**
	 * `x-default` points at the English page, which is what an unmatched locale
	 * should land on. Only emitted when there is more than one locale to choose
	 * between.
	 */
	const defaultAlternate = $derived(alternates.find((a) => a.locale === 'en'));

	/** Normalised to an array so the graph shape is decided in one place. */
	const graph = $derived(jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []);
	const structuredData = $derived(
		graph.length
			? JSON.stringify(
					graph.length === 1 && graph[0]['@context']
						? graph[0]
						: { '@context': 'https://schema.org', '@graph': graph }
				)
			: undefined
	);
</script>

<svelte:head>
	<title>{fullTitle}</title>
	{#if description}<meta name="description" content={description} />{/if}
	<link rel="canonical" href={canonical} />
	{#if noindex}
		<meta name="robots" content="noindex, follow" />
	{:else}
		<!--
			Opt in to full-size image and text previews. Without `max-image-preview`
			Google may show only a thumbnail, which for a seed catalogue is the
			difference between a browsable result and an ignorable one.
		-->
		<meta
			name="robots"
			content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
		/>
	{/if}

	{#each alternates as alternate (alternate.locale)}
		<link rel="alternate" hreflang={HREFLANG[alternate.locale]} href={absolute(alternate.path)} />
	{/each}
	{#if alternates.length > 1 && defaultAlternate}
		<link rel="alternate" hreflang="x-default" href={absolute(defaultAlternate.path)} />
	{/if}

	<meta property="og:site_name" content={SITE_NAME} />
	<meta property="og:type" content={type} />
	<meta property="og:title" content={fullTitle} />
	{#if description}<meta property="og:description" content={description} />{/if}
	<meta property="og:url" content={canonical} />
	<meta property="og:image" content={ogImage} />
	<!--
		Dimensions only for the generated cards, whose size this repository controls
		and which are always 1200×630. Declaring them lets Facebook and LinkedIn lay
		the preview out before the image has been fetched; declaring them wrongly for
		a photograph of unknown size would be worse than saying nothing.
	-->
	{#if card}
		<meta property="og:image:width" content="1200" />
		<meta property="og:image:height" content="630" />
		<meta property="og:image:type" content="image/jpeg" />
	{/if}
	<meta property="og:image:alt" content={imageAlt ?? title} />
	<meta property="og:locale" content={OG_LOCALE[locale] ?? locale} />
	{#if publishedTime}<meta property="article:published_time" content={publishedTime} />{/if}
	{#if modifiedTime}<meta property="article:modified_time" content={modifiedTime} />{/if}

	<meta name="twitter:card" content="summary_large_image" />
	<meta name="twitter:title" content={fullTitle} />
	{#if description}<meta name="twitter:description" content={description} />{/if}
	<meta name="twitter:image" content={ogImage} />
	<meta name="twitter:image:alt" content={imageAlt ?? title} />

	{#if structuredData}
		{@html `<script type="application/ld+json">${structuredData}<\/script>`}
	{/if}
</svelte:head>
